import axios, { type AxiosError } from 'axios';

// Create axios instance with cookie-based auth
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor - handle 401 redirect
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (!window.location.pathname.includes('/signin')) {
        window.location.href = '/signin';
      }
    }
    return Promise.reject(error);
  }
);

// Type-safe API error handler
export interface ApiError extends Error {
  message: string;
  status?: number;
  errors?: Record<string, string[]>;
  code?: string;
  detail?: Record<string, unknown>;
}

export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    // FastAPI HTTPException(detail=...) can pass either a string OR a dict
    // ({code, message, ...}). Normalize to the ApiError shape so callers can
    // branch on err.code === 'email_not_verified' etc. The full detail dict
    // is also forwarded as err.detail so callers can surface backend-only
    // fields (e.g. {missing, detected} on structural-validation errors).
    let message: string | undefined;
    let code: string | undefined;
    let detail: Record<string, unknown> | undefined;
    if (Array.isArray(data?.detail)) {
      // FastAPI body-validation 422s: detail is [{loc, msg, type}, ...]. `loc[0]` is
      // always the location kind ("body", "query", ...), so it's dropped.
      const items = data.detail as { loc?: (string | number)[]; msg?: string }[];
      message =
        items.length > 0
          ? items.map((item) => `${(item.loc ?? []).slice(1).join('.')}: ${item.msg}`).join('; ')
          : undefined;
    } else if (data?.detail && typeof data.detail === 'object') {
      message = data.detail.message;
      code = data.detail.code;
      detail = data.detail as Record<string, unknown>;
    } else if (typeof data?.detail === 'string') {
      message = data.detail;
    }
    const err = new Error(
      message || data?.message || error.message || 'An error occurred',
    ) as ApiError;
    err.status = error.response?.status;
    err.errors = data?.errors;
    err.code = code;
    err.detail = detail;
    return err;
  }

  if (error instanceof Error) {
    return error as ApiError;
  }

  return new Error('An unknown error occurred') as ApiError;
};

// Rate limiting helper
const requestTimestamps: Map<string, number[]> = new Map();

export const checkRateLimit = (
  endpoint: string,
  maxRequests: number = 5,
  windowMs: number = 60000
): boolean => {
  const now = Date.now();
  const timestamps = requestTimestamps.get(endpoint) || [];
  const validTimestamps = timestamps.filter((time) => now - time < windowMs);

  if (validTimestamps.length >= maxRequests) {
    return false;
  }

  validTimestamps.push(now);
  requestTimestamps.set(endpoint, validTimestamps);
  return true;
};
